import React from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  icon?: React.ReactNode;
  isLoading?: boolean;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  href,
  target,
  rel,
  icon,
  isLoading,
  fullWidth,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center gap-2 font-medium transition-all duration-300 ease-smooth rounded-full';
  
  const variantClasses = {
    primary: 'bg-apple-blue text-white hover:bg-apple-blue-hover hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100',
    secondary: 'bg-transparent border border-border-subtle text-text-primary hover:border-text-secondary hover:bg-bg-tertiary/50 disabled:opacity-50',
    ghost: 'bg-transparent text-text-primary hover:bg-bg-tertiary/50 disabled:opacity-50',
  };
  
  const sizeClasses = {
    sm: 'px-4 py-2 text-body-s',
    md: 'px-6 py-3 text-body-s',
    lg: 'px-7 py-3.5 text-body',
  };
  
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${fullWidth ? 'w-full' : ''} ${className}`;
  
  const content = (
    <>
      {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
      {!isLoading && icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
      {variant === 'primary' && !icon && !isLoading && (
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      )}
    </>
  );
  
  if (href) {
    return (
      <a href={href} target={target} rel={rel} className={`group ${classes}`}>
        {content}
      </a>
    );
  }
  
  return (
    <button 
      className={`group ${classes}`} 
      disabled={disabled || isLoading}
      {...props}
    >
      {content}
    </button>
  );
};

export default Button;
